import React from 'react'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../../../stores/CurrentCompanyStore'
import { ICompany } from '@getgreenline/homi-shared'
import { EditAuditProductRow } from './EditAuditProductRow'
import { CurrentUserStore } from '../../../../../../stores/CurrentUserStore'
import { Icon, Input, Empty } from 'antd'
import { debounce } from 'lodash'
import { AuditStore } from '../EditAuditStore'
import { AuditModels, AuditProductModels } from '@getgreenline/audits'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
  audit: AuditModels.CreateInventoryAudit
  company: ICompany
  editable: boolean
  auditStore: AuditStore
}

interface State {
  searchText?: string
}

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class EditAuditTableV1 extends React.Component<Props, State> {
  DEBOUNCE_TIME = 500

  searchAuditProduct = debounce((inputValue) => this.setFilter(inputValue), this.DEBOUNCE_TIME)

  constructor(props: Props) {
    super(props)

    this.state = {
      searchText: undefined,
    }
  }

  setFilter = (searchText: string) => {
    const trimmedSearchText = searchText.trim().toLowerCase()
    this.setState({ searchText: trimmedSearchText.length > 0 ? trimmedSearchText : undefined })
  }

  get containsBatchProduct(): boolean {
    return !!this.props.audit.products.find((product) => product.isBatchTracked)
  }

  get filteredAuditProducts(): AuditProductModels.EditAuditProduct[] {
    const { audit, auditStore } = this.props

    const searchText = this.state.searchText
    let filteredAuditProducts = auditStore.showAuditproductsWithoutSnapshot
      ? auditStore.auditProductsWithoutSnapshot
      : audit.products

    if (searchText) {
      filteredAuditProducts = filteredAuditProducts.filter((auditProduct) => {
        const lowerCasedProductName = auditProduct.productName.toLowerCase()
        const lowerCasedSku = (auditProduct.sku || '').toLowerCase()

        const shouldFiltered = searchText.split(' ').some((word: string) => {
          const lowerCasedWord = word.toLowerCase()
          return `${lowerCasedProductName} ${lowerCasedSku}`.includes(lowerCasedWord)
        })

        return shouldFiltered
      })
    }
    return filteredAuditProducts
  }

  render() {
    const { audit, editable } = this.props

    const borderTop = '1px solid #dee2e6'

    return (
      <>
        <div id='edit-audit-search'>
          <Input
            prefix={<Icon type='search' />}
            style={{ width: 260, marginBottom: 8 }}
            placeholder='Search by product name or SKU'
            onChange={(e) => this.searchAuditProduct(e.target.value)}
          />
        </div>

        <table
          className='table table-sm table-bordered gr-ant-table'
          style={{ position: 'relative' }}
        >
          <thead className='thead-light'>
            {this.props.auditStore.shouldDisplayQuantity ? (
              <>
                <tr>
                  <th rowSpan={2}>Product</th>
                  {this.containsBatchProduct && (
                    <th rowSpan={2} className='text-center'>
                      Lot #
                    </th>
                  )}
                  <th colSpan={2} className='text-center'>
                    Count
                  </th>
                  <th colSpan={2} className='text-center'>
                    Difference
                  </th>
                  <th rowSpan={2}>Notes</th>
                  {editable && audit.products.length > 0 && <th rowSpan={2}></th>}
                </tr>
                <tr>
                  <th className='text-right' style={{ borderTop }}>
                    Counted
                  </th>
                  <th className='text-right' style={{ borderTop }}>
                    Expected
                  </th>
                  <th className='text-right' style={{ borderTop }}>
                    Quantity
                  </th>
                  <th className='text-right' style={{ borderTop }}>
                    Cost
                  </th>
                </tr>
              </>
            ) : (
              <tr>
                <th>Product</th>
                {this.containsBatchProduct && <th className='text-center'>Lot #</th>}
                <th className='text-center'>Count</th>
                <th>Notes</th>
                <th></th>
              </tr>
            )}
          </thead>

          <tbody>
            {this.filteredAuditProducts.length > 0 ? (
              this.filteredAuditProducts.map((product) => {
                return (
                  <EditAuditProductRow
                    key={product.productId}
                    product={product}
                    audit={audit}
                    isAuditEditable={editable}
                    containsBatchProduct={this.containsBatchProduct}
                    auditStore={this.props.auditStore}
                    searchText={this.state.searchText}
                  />
                )
              })
            ) : (
              <tr className='no-row'>
                <td colSpan={7}>
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='No products added yet' />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </>
    )
  }
}
